import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { 
  X, Plus, Trash2, Mail, UserPlus, Users, Loader2, 
  Crown, AlertCircle, Check
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function TeamManagement({ onClose }) {
  const [members, setMembers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [maxMembers, setMaxMembers] = useState(0);
  const [currentCount, setCurrentCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('members');
  
  // Invite form
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviting, setInviting] = useState(false);
  
  // Create form
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [createName, setCreateName] = useState('');
  const [createEmail, setCreateEmail] = useState('');
  const [createPassword, setCreatePassword] = useState('');
  const [creating, setCreating] = useState(false);
  
  const [message, setMessage] = useState('');

  const loadTeam = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/team/members`);
      setMembers(res.data.members || []);
      setInvites(res.data.invites || []);
      setMaxMembers(res.data.max_members || 0);
      setCurrentCount(res.data.current_count || 0);
    } catch (err) {
      console.error('Load team error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadTeam(); }, [loadTeam]);

  const inviteMember = async () => {
    if (!inviteEmail.trim()) return;
    setInviting(true);
    setMessage('');
    try {
      const res = await axios.post(`${API}/team/invite`, { email: inviteEmail.trim() });
      setMessage(res.data.message);
      setInviteEmail('');
      loadTeam();
    } catch (err) {
      setMessage(err.response?.data?.detail || 'Failed to invite');
    } finally {
      setInviting(false);
    }
  };

  const createMember = async () => {
    if (!createName.trim() || !createEmail.trim() || !createPassword.trim()) return;
    setCreating(true);
    setMessage('');
    try {
      const res = await axios.post(`${API}/team/create-member`, {
        name: createName.trim(),
        email: createEmail.trim(),
        password: createPassword.trim(),
      });
      setMessage(res.data.message);
      setCreateName('');
      setCreateEmail('');
      setCreatePassword('');
      setShowCreateForm(false);
      loadTeam();
    } catch (err) {
      setMessage(err.response?.data?.detail || 'Failed to create member');
    } finally {
      setCreating(false);
    }
  };

  const removeMember = async (id) => {
    if (!window.confirm('Remove this team member?')) return;
    try {
      await axios.delete(`${API}/team/members/${id}`);
      loadTeam();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to remove');
    }
  };

  const cancelInvite = async (id) => {
    try {
      await axios.delete(`${API}/team/invites/${id}`);
      loadTeam();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to cancel');
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="team-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[85vh] flex flex-col">
        {/* Header */}
        <div className="p-5 border-b border-[#E2DFD8]">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-xl font-bold text-[#1B3A6B]">Team Management</h2>
            <button onClick={onClose} className="p-2 hover:bg-[#F4F2EC] rounded-lg" data-testid="team-close-btn">
              <X className="w-5 h-5 text-[#475569]" />
            </button>
          </div>
          
          {/* Seat counter */}
          {maxMembers > 0 ? (
            <div className="flex items-center gap-2 text-xs text-[#475569]">
              <Users className="w-3.5 h-3.5" />
              <span>{currentCount} / {maxMembers} seats used</span>
              <div className="flex-1 h-1.5 bg-[#F4F2EC] rounded-full overflow-hidden ml-2">
                <div
                  className={`h-full rounded-full ${currentCount >= maxMembers ? 'bg-red-500' : 'bg-[#1B3A6B]'}`}
                  style={{ width: `${Math.min(100, (currentCount / maxMembers) * 100)}%` }}
                />
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-2 text-xs text-amber-600 bg-amber-50 px-3 py-2 rounded-lg">
              <AlertCircle className="w-3.5 h-3.5" />
              Upgrade to Church or Organization to add team members.
            </div>
          )}

          {/* Tabs */}
          <div className="flex gap-2 mt-3">
            <button
              onClick={() => setTab('members')}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                tab === 'members' ? 'bg-[#1B3A6B] text-white' : 'bg-[#F4F2EC] text-[#475569]'
              }`}
              data-testid="team-tab-members"
            >
              Members ({members.length})
            </button>
            <button
              onClick={() => setTab('invite')}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                tab === 'invite' ? 'bg-[#1B3A6B] text-white' : 'bg-[#F4F2EC] text-[#475569]'
              }`}
              data-testid="team-tab-invite"
            >
              Invite / Add
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {message && (
            <div className="mb-3 p-2.5 bg-blue-50 border border-blue-200 text-blue-700 rounded-lg text-xs flex items-center gap-2" data-testid="team-message">
              <Check className="w-3.5 h-3.5" /> {message}
            </div>
          )}

          {loading ? (
            <div className="text-center py-12"><Loader2 className="w-6 h-6 animate-spin text-[#1B3A6B] mx-auto" /></div>
          ) : tab === 'members' ? (
            <div className="space-y-2">
              {members.length === 0 ? (
                <p className="text-center text-sm text-[#94A3B8] py-8">No team members yet.</p>
              ) : members.map(m => (
                <div key={m.id || m.email} className="flex items-center gap-3 p-3 bg-[#F9F8F6] rounded-xl group" data-testid={`team-member-${m.email}`}>
                  <div className="w-9 h-9 rounded-full bg-[#1B3A6B] flex items-center justify-center text-white text-sm font-medium">
                    {(m.name || m.email)[0].toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-[#0F1A2A] truncate">{m.name || m.email}</p>
                    <p className="text-xs text-[#94A3B8] truncate">{m.email}</p>
                  </div>
                  <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${
                    m.role === 'platform_admin' || m.role === 'admin' 
                      ? 'bg-[#B8860B]/10 text-[#B8860B]' 
                      : 'bg-[#F4F2EC] text-[#475569]'
                  }`}>
                    {m.role === 'platform_admin' ? 'Admin' : m.role === 'admin' ? 'Admin' : 'Member'}
                  </span>
                  {m.role !== 'platform_admin' && m.role !== 'admin' && (
                    <button onClick={() => removeMember(m.id)} className="opacity-0 group-hover:opacity-100 p-1 hover:bg-red-50 rounded" data-testid={`remove-member-${m.email}`}>
                      <Trash2 className="w-3.5 h-3.5 text-[#94A3B8] hover:text-red-500" />
                    </button>
                  )}
                </div>
              ))}
              
              {/* Pending invites */}
              {invites.length > 0 && (
                <div className="mt-4">
                  <p className="text-xs font-medium text-[#94A3B8] uppercase tracking-wide mb-2">Pending Invites</p>
                  {invites.map(inv => (
                    <div key={inv.id} className="flex items-center gap-3 p-3 bg-amber-50/50 border border-amber-100 rounded-xl group mb-2">
                      <Mail className="w-4 h-4 text-amber-500" />
                      <div className="flex-1">
                        <p className="text-sm text-[#0F1A2A]">{inv.email}</p>
                        <p className="text-[10px] text-[#94A3B8]">Invited by {inv.invited_by}</p>
                      </div>
                      <button onClick={() => cancelInvite(inv.id)} className="text-xs text-[#94A3B8] hover:text-red-500">Cancel</button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : (
            /* Invite / Add tab */
            <div className="space-y-5">
              {/* Invite by email */}
              <div>
                <h4 className="text-sm font-semibold text-[#0F1A2A] mb-2 flex items-center gap-2">
                  <Mail className="w-4 h-4 text-[#1B3A6B]" /> Invite by Email
                </h4>
                <div className="flex gap-2">
                  <input
                    value={inviteEmail}
                    onChange={e => setInviteEmail(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && inviteMember()}
                    placeholder="team.member@church.org"
                    className="flex-1 px-3 py-2.5 text-sm border border-[#E2DFD8] rounded-lg focus:ring-2 focus:ring-[#1B3A6B]"
                    data-testid="invite-email-input"
                  />
                  <button
                    onClick={inviteMember}
                    disabled={inviting || !inviteEmail.trim() || maxMembers === 0}
                    className="flex items-center gap-1.5 px-4 py-2.5 bg-[#1B3A6B] text-white rounded-lg text-sm font-medium disabled:opacity-50 hover:bg-[#254B8A]"
                    data-testid="send-invite-btn"
                  >
                    {inviting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
                    Invite
                  </button>
                </div>
              </div>

              <div className="border-t border-[#E2DFD8]" />

              {/* Create account directly */}
              <div>
                <h4 className="text-sm font-semibold text-[#0F1A2A] mb-2 flex items-center gap-2">
                  <UserPlus className="w-4 h-4 text-[#1B3A6B]" /> Create Account Directly
                </h4>
                {showCreateForm ? (
                  <div className="space-y-2" data-testid="create-member-form">
                    <input
                      value={createName}
                      onChange={e => setCreateName(e.target.value)}
                      placeholder="Full name"
                      className="w-full px-3 py-2.5 text-sm border border-[#E2DFD8] rounded-lg"
                      data-testid="create-name-input"
                    />
                    <input
                      value={createEmail}
                      onChange={e => setCreateEmail(e.target.value)}
                      placeholder="Email"
                      type="email"
                      className="w-full px-3 py-2.5 text-sm border border-[#E2DFD8] rounded-lg"
                      data-testid="create-email-input"
                    />
                    <input
                      value={createPassword}
                      onChange={e => setCreatePassword(e.target.value)}
                      placeholder="Password"
                      type="password"
                      className="w-full px-3 py-2.5 text-sm border border-[#E2DFD8] rounded-lg"
                      data-testid="create-password-input"
                    />
                    <div className="flex gap-2 justify-end">
                      <button onClick={() => setShowCreateForm(false)} className="text-xs text-[#94A3B8]">Cancel</button>
                      <button
                        onClick={createMember}
                        disabled={creating || !createName.trim() || !createEmail.trim() || !createPassword.trim() || maxMembers === 0}
                        className="flex items-center gap-1.5 px-4 py-2 bg-[#1B3A6B] text-white rounded-lg text-sm font-medium disabled:opacity-50"
                        data-testid="create-member-btn"
                      >
                        {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />}
                        Create
                      </button>
                    </div>
                  </div>
                ) : (
                  <button
                    onClick={() => setShowCreateForm(true)}
                    disabled={maxMembers === 0}
                    className="w-full flex items-center justify-center gap-2 py-2.5 border-2 border-dashed border-[#E2DFD8] rounded-lg text-sm text-[#475569] hover:border-[#1B3A6B] disabled:opacity-50"
                    data-testid="show-create-form-btn"
                  >
                    <UserPlus className="w-4 h-4" /> Create a member account
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
